<script setup lang="ts">
import { onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import AppSidebar from '@/components/core/shell/AppSidebar.vue';
import DepotTopbar from '@/components/depot/shell/DepotTopbar.vue';
import DrawerSitePlaceDetails from '@/components/depot/DrawerSitePlaceDetails.vue';
import { useDepotTour } from '@/composables/useDepotTour';

/*
 * The tours are driven from the layout rather than from any page, because this is the one
 * component every depot route passes through and, unlike the RouterView below it, it does
 * not remount on navigation. A runner living in a page would be destroyed by the very
 * navigation it needs to react to.
 */
const route = useRoute();
const { maybeAutoStart, stopTour, sweepAnchors } = useDepotTour();

onMounted(() => {
	sweepAnchors(route.name as string | undefined);
	return maybeAutoStart(route.name as string | undefined);
});

watch(
	() => {
		return route.fullPath;
	},
	() => {
		// The RouterView is keyed on fullPath, so the page a running tour is anchored to has
		// just been destroyed. Stop rather than trying to follow it.
		stopTour();
		sweepAnchors(route.name as string | undefined);
		void maybeAutoStart(route.name as string | undefined);
	},
);
</script>

<template>
    <div class="depot-app-root grid min-h-screen bg-[color:var(--bg)]">
        <AppSidebar />
        <div class="flex min-w-0 flex-col">
            <DepotTopbar />
            <main class="mx-auto w-full max-w-[1320px] px-8 pb-24 pt-7 max-md:px-4 max-md:pb-20">
                <RouterView v-slot="{ Component }" :key="$route.fullPath">
                    <transition name="fade" mode="out-in">
                        <component :is="Component"></component>
                    </transition>
                </RouterView>
            </main>
        </div>
        <DrawerSitePlaceDetails />
    </div>
</template>

<style scoped>
/* grid-template-columns can't be expressed with a single Tailwind utility
   when the value is a CSS variable; using a one-line style block keeps it
   in this component rather than in a global stylesheet. */
.depot-app-root {
    grid-template-columns: var(--sidebar-w) 1fr;
}
@media (max-width: 980px) {
    .depot-app-root {
        grid-template-columns: 64px 1fr;
    }
}
@media (max-width: 760px) {
    .depot-app-root {
        grid-template-columns: 1fr;
    }
}
</style>
